import React, { useState } from 'react';
import useBillingAddressContainer from '../../../Hooks/useBillingAddressContainer';
import { ItemScheme } from '../../../../Form/MagicFormItem';
import { useEffectAsync } from '../../../../../helpers';
import MagicForm from '../../../../Form/MagicForm';
import apiClient from '../../../../../Services/ApiClient';
import useApiClient from '../../../../../Modules/Base/Hooks/useApiClient';
import { useAppContext } from '../../../../../Services/AppContext';
import {
	AddressType,
	useLazyWorkplaceBillingAddressQuery,
	useLazyWorkplaceUseCustomBillingAddressQuery,
} from '../../../../../graphql/graphql';
import Loading from '../../../../Loading';
import useNotification from '../../../../../Hooks/useNotification.hook';
import useTranslation from '../../../../../Hooks/useTranslation';
import RootBillingAddress from './RootBillingAddress';
import { MagicField } from '../../../../../Enums/MagicField.enum';

interface Props {
	isSuperAdmin?: boolean;
	rootWorkplaceId: string;
}

const BillingAddressSetup: React.FC<Props> = ({
	isSuperAdmin,
	rootWorkplaceId,
}) => {
	const [formScheme, setFormScheme] = useState<ItemScheme[]>([]);
	const [defaultValues, setDefaultValues] = useState({});
	const [isLoading, setIsLoading] = useState(false);
	const { workplaceId } = useAppContext();
	const { billingAddressContainerScheme } = useBillingAddressContainer();
	const { displayError } = useApiClient();
	const { showNotificationSuccess } = useNotification();
	const { t } = useTranslation();
	const [getRootBilling] = useLazyWorkplaceUseCustomBillingAddressQuery();
	const [getBillingAddress] = useLazyWorkplaceBillingAddressQuery();

	const wrkId = isSuperAdmin ? rootWorkplaceId : workplaceId;

	const loadDefaultValues = async () => {
		setIsLoading(true);
		const defaultValues: {
			billingAddress?: object;
			useCustomBillingAddress: boolean;
		} = {
			billingAddress: undefined,
			useCustomBillingAddress: false,
		};
		if (wrkId) {
			const workplaceBillingAddress = await getBillingAddress({
				where: {
					workplaceId: wrkId,
					type: AddressType.Billing,
				},
			});
			const billingAddress = workplaceBillingAddress?.data?.workplaceAddress;
			if (billingAddress) {
				defaultValues.billingAddress = billingAddress;
			}
			const result = await getRootBilling({ id: wrkId });
			if (result) {
				defaultValues.useCustomBillingAddress =
					!result.data?.workplace?.rootBilling;
			}
			setDefaultValues(defaultValues);
			setIsLoading(false);
		}
	};

	const handleSubmit = async (values: any) => {
		setIsLoading(true);
		await handleUseCustomBillingAddress(values?.useCustomBillingAddress);
		try {
			const response = await apiClient.patch(`workplaces/${wrkId}`, values);
			if (response.status === 200) {
				showNotificationSuccess({
					content: t('base.saved'),
				});
				await loadDefaultValues();
			}
		} catch (e) {
			displayError(e);
		}
		setIsLoading(false);
	};

	useEffectAsync(async () => {
		const scheme = await billingAddressContainerScheme();
		if (scheme) {
			const finalScheme: ItemScheme[] = [
				{
					type: MagicField.CHECKBOX,
					title:
						'component.rootBillingAddress.iWannaUseOtherBillingAddressForThisWorkplace',
					key: 'useCustomBillingAddress',
				},
				{
					type: MagicField.CONDITION,
					settings: {
						rules: [
							{
								formControlKey: 'useCustomBillingAddress',
								rule: 'equal',
								value: true,
							},
						],
					},
					subType: 'view',
					children: scheme,
				},
			];
			setFormScheme(isSuperAdmin ? scheme : finalScheme);
		}
		await loadDefaultValues();
	}, []);

	const handleUseCustomBillingAddress = async (
		useCustomBillingAddress: boolean,
	) => {
		try {
			await apiClient.patch(`/workplaces/${wrkId}`, {
				rootBilling: !useCustomBillingAddress,
			});
		} catch (e) {
			displayError(e);
		}
	};

	if (isLoading) {
		return <Loading />;
	}

	return (
		<>
			{!isSuperAdmin && <RootBillingAddress />}
			<div className='mt-3'>
				<MagicForm
					formScheme={formScheme}
					defaultValues={defaultValues}
					onSubmit={handleSubmit}
				/>
			</div>
		</>
	);
};

export default BillingAddressSetup;
